/**
 * SASADM-0152 — the product-console hostnames table, moved out of `ProductAdmins.tsx`
 * (SASADM-0148) so the Product Admin and Reseller Admin panels share it. Behavior is
 * unchanged: full-URL link when verified, plain monospace text when not, a copy button on
 * both; Type and Status columns; Check DNS / Re-check with `DnsRowResult` on a mismatch,
 * the not-configured message when `configured: false`, the row flipping in place on a
 * match, and the Deploy Platform Routing reminder after a successful check.
 *
 * The owning panel fetches `productHostnames.list(productId)` (like the rest of its data)
 * and hands the whole list in; the table shows ONLY the rows whose `appId` matches, so
 * each console sees its own hosts. Derived only — no add/delete/primary controls.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { apiAdmin } from '@/api/admin';
import type { DnsCheckResult } from '@/api/admin/hostnames';
import { ApiError } from '@/api/errors';
import type { ProductHostnameDto } from '@/api/types';
import { CopyToClipboardButton } from '@/components/CopyToClipboardButton';
import { DnsRowResult } from '@/components/DnsRowResult';
import { useSnackbar } from '@/components/Snackbar';
import { useCopy } from '@/hooks/useCopy';

// SASADM-0148 — copied verbatim from TenantHostnames.tsx (no shared module; same
// precedent as that file's own TYPE_ORDER comment).
function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export interface ConsoleHostnamesTableProps {
  productId: number;
  /** Only rows whose `appId` matches are shown. */
  appId: number;
  /** The product's full hostname list; `null` while loading. */
  hostnames: ProductHostnameDto[] | null;
  loadFailed: boolean;
  /** Labels a row "Platform default". */
  isPlatformRow: (row: ProductHostnameDto) => boolean;
  /** The copy line above the table. */
  explainer: string;
  /** A Check DNS came back verified — replace this row in the owner's list. */
  onRowVerified: (row: ProductHostnameDto) => void;
}

export function ConsoleHostnamesTable({
  productId,
  appId,
  hostnames,
  loadFailed,
  isPlatformRow,
  explainer,
  onRowVerified,
}: ConsoleHostnamesTableProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  // Per-row DNS-check state (Spec 0028 pattern, mirrored from TenantHostnames) +
  // a one-way reminder once any row's check comes back verified.
  const [dnsChecking, setDnsChecking] = useState<Record<number, boolean>>({});
  const [dnsResults, setDnsResults] = useState<Record<number, DnsCheckResult>>({});
  const [showDeployReminder, setShowDeployReminder] = useState(false);

  const rows = hostnames?.filter((row) => row.appId === appId) ?? null;

  // Decision — Check DNS mirrors TenantHostnames' runVerify: per-row checking/result
  // state, DnsRowResult for presentation. `res.check.match` (or the returned row's
  // `dnsVerified`) flips that row in the owner's list without a reload; a successful
  // (matched) check also surfaces the Deploy Platform Routing reminder, since a newly
  // verified console hostname isn't live until routing is deployed.
  async function handleVerifyDns(row: ProductHostnameDto) {
    setDnsChecking((m) => ({ ...m, [row.id]: true }));
    try {
      const res = await apiAdmin.productHostnames.verifyDns(productId, row.id);
      if (res.check.match || res.hostname.dnsVerified) {
        onRowVerified(res.hostname);
        setDnsResults((m) => {
          const next = { ...m };
          delete next[row.id];
          return next;
        });
        setShowDeployReminder(true);
      } else {
        setDnsResults((m) => ({ ...m, [row.id]: res.check }));
      }
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
      else snack.error(t('errors.generic', 'Something went wrong. Please try again.'));
    } finally {
      setDnsChecking((m) => ({ ...m, [row.id]: false }));
    }
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {explainer}
      </Typography>
      {loadFailed ? (
        <Alert severity="error">
          {t('products.productAdmins.hostnames.loadError', "Console hostnames couldn't be loaded.")}
        </Alert>
      ) : rows === null ? (
        <Typography color="text.secondary">{t('common.loading', 'Loading')}</Typography>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">
          {t('products.productAdmins.hostnames.empty', 'No console hostnames yet.')}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t('products.productAdmins.hostnames.columns.hostname', 'Hostname')}
                </TableCell>
                <TableCell>{t('products.productAdmins.hostnames.columns.type', 'Type')}</TableCell>
                <TableCell>
                  {t('products.productAdmins.hostnames.columns.status', 'Status')}
                </TableCell>
                <TableCell>{t('common.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const url = `https://${row.hostname}`;
                const checking = Boolean(dnsChecking[row.id]);
                const dnsResult = dnsResults[row.id];
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        {row.dnsVerified ? (
                          <Link
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {url}
                          </Link>
                        ) : (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {url}
                          </Typography>
                        )}
                        <CopyToClipboardButton value={url} ariaLabel={t('common.copy', 'Copy')} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <Typography variant="body2">{row.hostnameType}</Typography>
                        {isPlatformRow(row) ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t(
                              'products.productAdmins.hostnames.platformDefault',
                              'Platform default',
                            )}
                          />
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {row.dnsVerified ? (
                        <Typography variant="body2">
                          {t(
                            'products.productAdmins.hostnames.status.verified',
                            'Verified · {{date}}',
                            {
                              date: row.dnsVerifiedAt ? fmtDate(row.dnsVerifiedAt) : '',
                            },
                          )}
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'warning.main' }}>
                          {t('products.productAdmins.hostnames.status.unverified', 'Unverified')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => void handleVerifyDns(row)}
                        disabled={checking}
                        startIcon={checking ? <CircularProgress size={14} /> : null}
                      >
                        {row.dnsVerified
                          ? t('products.productAdmins.hostnames.recheck', 'Re-check')
                          : t('products.productAdmins.hostnames.checkDns', 'Check DNS')}
                      </Button>
                      {dnsResult ? (
                        dnsResult.configured === false ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {t(
                              'products.productAdmins.hostnames.notConfigured',
                              'DNS verification is not configured on this environment',
                            )}
                          </Typography>
                        ) : (
                          <DnsRowResult result={dnsResult} />
                        )
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {showDeployReminder ? (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {t(
            'products.productAdmins.hostnames.deployReminder',
            'DNS verified. Deploy routing to make this hostname live: Operations → Deploy Platform Routing.',
          )}
        </Alert>
      ) : null}
    </Box>
  );
}
